/**
 * PATH: packages/ui/src/components/settings/AiUsageDashboard.tsx
 * PURPOSE:
 *   Universal AI usage & billing dashboard. Same component shape that
 *   originally shipped in quotech-frontend/src/Pages/Components/settings/
 *   AiUsageDashboard.jsx, ported to TypeScript and decoupled from any
 *   specific HTTP client via the `AiUsageApi` adapter (see ./types.ts).
 *
 *   This file is the presentational orchestrator. To stay under the
 *   line limit it was split (god-component split, zero behaviour change,
 *   markup byte-identical) into:
 *     * useAiUsageDashboard.ts — all state/effects/fetch/CSV/derived.
 *     * AiUsageEventTable.tsx  — the "recent inference events" section.
 *     * aiUsageFormat.ts       — pure formatters + window constants.
 *   The public API (AiUsageDashboard, AiUsageDashboardProps, default
 *   export) is unchanged for the three consuming host apps.
 *
 * ROLE IN ARCHITECTURE:
 *   The single source of truth for "show me what AI we ran on this org".
 *   Three host apps consume it:
 *     * quotech-frontend → Django billing endpoints
 *     * ass-dashboard    → FastAPI ai_billing endpoints
 *     * quotech-admin    → Django billing (platform_summary mode)
 *
 *   Every `AiUsageApi` resolves to the same response shape so the dashboard
 *   never branches on which backend served it.
 *
 * NON-RESPONSIBILITIES:
 *   - Does NOT compute pricing client-side. Server is source of truth.
 *   - Does NOT show the upstream provider name. Backends strip it.
 *   - Does NOT surface the platform's markup multiple to the customer.
 *     Markup is an operator pricing lever — exposing "we charge 2.0× our
 *     cost" on the customer-facing panel reads as adversarial AND leaks
 *     commercially-sensitive cost basis. Operator-only views (the
 *     0x3admin CustomerAiUsage page) DO show markup + raw provider cost
 *     + margin; that lives in its own component in `apps/admin/`. This
 *     component must stay silent about markup forever. See
 *     `customer-facing-pricing-doesnt-leak-cost-basis` in
 *     `docs/bugs/ROBUSTNESS_PRINCIPLES.md` for the rationale and the
 *     regression test that pins this contract.
 *
 * NOTES:
 *   - Endpoints are owner/admin-only — any 403-shaped error is rendered as a
 *     friendly "ask your owner" message instead of a hard error.
 *   - Defaults to a 30-day window. Page size is 50, capped server-side.
 */

import {
  Activity,
  AlertCircle,
  Download,
  Filter,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  Search,
  Sparkles,
  Zap,
} from 'lucide-react'
import { cn } from '../../lib/utils'
import { AiUsageEventTable } from './AiUsageEventTable'
import { PERIOD_OPTIONS, formatNumber, formatUSD } from './aiUsageFormat'
import { useAiUsageDashboard } from './useAiUsageDashboard'
import type { AiUsageApi, AiUsageEventRow } from './types'

export interface AiUsageDashboardProps {
  /** Required — empty values render nothing. */
  organisationId: string | number | null | undefined
  /** Used in the CSV filename only. */
  organisationName?: string | null
  /** Adapter that talks to whichever backend serves this host app. */
  api: AiUsageApi
  /** Optional override for the heading shown above the dashboard. */
  title?: string
  /** Optional override for the subtitle. */
  subtitle?: string
  /** Optional handler for non-403 errors (e.g. toast). */
  onCsvError?: (err: unknown) => void
  /** Tailwind className passthrough for the outer Card. */
  className?: string
  /**
   * Background poll interval, in milliseconds. The dashboard will
   * silently refetch the current page at this cadence so that
   * inference activity surfaces "live" without the user having to
   * click Refresh. Set to 0 (or omit) to disable.
   *
   * Defaults to 30000 (30s) when omitted — fast enough to feel
   * responsive, slow enough to keep the FastAPI billing endpoint
   * load negligible.
   */
  autoRefreshIntervalMs?: number
  /**
   * Whether the auto-refresh starts in the "on" position. The user
   * can toggle it from the toolbar button regardless. Defaults to
   * true so opening the panel kicks off live updates.
   */
  autoRefreshDefaultOn?: boolean
  /**
   * Called once per refresh with rows that did NOT exist in the
   * previous fetch. Host apps wire this to their notification /
   * toast system so newly-completed AI calls bubble up to the
   * top-nav bell instead of being trapped on this single panel.
   *
   * Note: only fires AFTER the first successful fetch so we don't
   * fire-hose a notification per pre-existing row on initial load.
   */
  onNewActivity?: (rows: AiUsageEventRow[]) => void
}

export function AiUsageDashboard({
  organisationId,
  organisationName,
  api,
  title = 'QuotechAI usage & billing',
  subtitle = 'Every AI inference call we run on your behalf, the tokens it cost, and the exact USD billed. Audit any line, export the lot to CSV.',
  onCsvError,
  className,
  autoRefreshIntervalMs = 30000,
  autoRefreshDefaultOn = true,
  onNewActivity,
}: AiUsageDashboardProps) {
  const {
    days,
    setDays,
    taskFilter,
    setTaskFilter,
    successFilter,
    setSuccessFilter,
    search,
    setSearch,
    searchInput,
    setSearchInput,
    page,
    setPage,
    loading,
    error,
    autoRefreshOn,
    setAutoRefreshOn,
    lastFetchedAt,
    lastFetchedLabel,
    summary,
    breakdown,
    rows,
    pagination,
    taskOptions,
    fetchUsage,
    handleSearchSubmit,
    downloadCsv,
  } = useAiUsageDashboard({
    organisationId,
    organisationName,
    api,
    onCsvError,
    autoRefreshIntervalMs,
    autoRefreshDefaultOn,
    onNewActivity,
  })

  if (!organisationId) return null

  return (
    <div
      className={cn(
        'rounded-lg p-6 border border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/70',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Activity className="w-4 h-4 text-violet-500" />
            {title}
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">
            {subtitle}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
            {PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.days}
                type="button"
                onClick={() => setDays(opt.days)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium transition-colors',
                  days === opt.days
                    ? 'bg-violet-500 text-white'
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {/* Live indicator + manual refresh + auto-refresh toggle.
              We keep these in a single visual cluster so the user's
              eye finds the "is data fresh?" answer in one glance. */}
          <div className="inline-flex items-center gap-1 rounded-md border border-slate-200 dark:border-slate-700 px-2 h-8 text-[11px] text-slate-500">
            <span
              className={cn(
                'h-1.5 w-1.5 rounded-full',
                autoRefreshOn
                  ? 'bg-emerald-500 animate-pulse'
                  : 'bg-slate-400',
              )}
              aria-hidden="true"
            />
            <span>
              {autoRefreshOn ? 'Live' : 'Paused'}
              {lastFetchedAt && (
                <>
                  {' · '}
                  <span className="text-slate-400">{lastFetchedLabel}</span>
                </>
              )}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setAutoRefreshOn((v) => !v)}
            disabled={!autoRefreshIntervalMs || autoRefreshIntervalMs <= 0}
            title={
              autoRefreshOn
                ? `Pause auto-refresh (currently every ${Math.round(autoRefreshIntervalMs / 1000)}s)`
                : `Resume auto-refresh (every ${Math.round(autoRefreshIntervalMs / 1000)}s)`
            }
            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-slate-200 dark:border-slate-700 text-xs hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {autoRefreshOn ? (
              <>
                <Pause className="w-3.5 h-3.5" /> Pause
              </>
            ) : (
              <>
                <Play className="w-3.5 h-3.5" /> Live
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => fetchUsage()}
            disabled={loading}
            className="inline-flex items-center gap-2 h-8 px-3 rounded-md border border-slate-200 dark:border-slate-700 text-xs hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
          >
            <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
            Refresh
          </button>
          <button
            type="button"
            onClick={downloadCsv}
            className="inline-flex items-center gap-2 h-8 px-3 rounded-md border border-slate-200 dark:border-slate-700 text-xs hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
        </div>
      </div>

      {/* Summary tiles.
          NOTE - customer-facing tiles only. Operator pricing levers
          (markup multiple, raw provider cost, margin) live exclusively
          in the 0x3admin CustomerAiUsage view. See the file-header
          NON-RESPONSIBILITIES block for the contract and the regression
          test in `AiUsageDashboard.test.tsx` that pins it. */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
          <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Calls</p>
            <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
              {formatNumber(summary.calls)}
            </p>
          </div>
          <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Tokens</p>
            <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
              {formatNumber(summary.tokens_total)}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">
              in {formatNumber(summary.tokens_input)} · out{' '}
              {formatNumber(summary.tokens_output)}
            </p>
          </div>
          <div className="rounded-lg border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/40 dark:bg-emerald-900/10 p-3">
            <p className="text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
              Spend (billed)
            </p>
            <p className="text-xl font-bold text-emerald-700 dark:text-emerald-400">
              {formatUSD(summary.cost_usd_billed)}
            </p>
          </div>
        </div>
      )}

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2 mt-5">
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <Filter className="w-3.5 h-3.5" />
          Filters
        </div>
        <select
          value={taskFilter}
          onChange={(e) => setTaskFilter(e.target.value)}
          className="text-xs h-8 px-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300"
        >
          <option value="">All tasks</option>
          {taskOptions.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          value={successFilter}
          onChange={(e) =>
            setSuccessFilter(e.target.value as '' | 'true' | 'false')
          }
          className="text-xs h-8 px-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300"
        >
          <option value="">All statuses</option>
          <option value="true">Success</option>
          <option value="false">Errors only</option>
        </select>
        <form onSubmit={handleSearchSubmit} className="flex items-center gap-1">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search context_id / query"
              className="h-8 pl-7 pr-2 text-xs w-64 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300"
            />
          </div>
          <button
            type="submit"
            className="h-8 px-3 rounded-md border border-slate-200 dark:border-slate-700 text-xs hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Search
          </button>
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('')
                setSearchInput('')
              }}
              className="h-8 px-2 text-xs text-slate-500 hover:text-slate-700"
            >
              Clear
            </button>
          )}
        </form>
      </div>

      {/* Loading / error */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
        </div>
      ) : error ? (
        <div className="mt-5 rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50/60 dark:bg-amber-900/10 p-4 flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
          <p className="text-sm text-amber-800 dark:text-amber-300">{error}</p>
        </div>
      ) : (
        <>
          {/* Top tasks by spend */}
          {breakdown.length > 0 && (
            <div className="mt-6">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5" />
                Top tasks by spend
              </h4>
              <div className="space-y-2">
                {breakdown.slice(0, 8).map((b) => {
                  const max = Number(breakdown[0]?.cost_usd_billed || 0) || 1
                  const pct = (Number(b.cost_usd_billed || 0) / max) * 100
                  return (
                    <div
                      key={b.task_type}
                      className="rounded-md border border-slate-200 dark:border-slate-800 px-3 py-2"
                    >
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-3.5 h-3.5 text-violet-400" />
                          <span className="font-medium text-slate-800 dark:text-slate-200">
                            {b.task_label}
                          </span>
                          <span className="text-slate-400">
                            · {formatNumber(b.calls)} calls ·{' '}
                            {formatNumber(b.tokens_total)} tokens
                          </span>
                        </div>
                        <span className="font-mono text-slate-700 dark:text-slate-200">
                          {formatUSD(b.cost_usd_billed)}
                        </span>
                      </div>
                      <div className="h-1.5 mt-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-violet-400 to-emerald-400"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Event log */}
          <AiUsageEventTable
            rows={rows}
            pagination={pagination}
            days={days}
            autoRefreshOn={autoRefreshOn}
            autoRefreshIntervalMs={autoRefreshIntervalMs}
            page={page}
            loading={loading}
            setDays={setDays}
            setPage={setPage}
          />
        </>
      )}
    </div>
  )
}

export default AiUsageDashboard
